import { Component, ChangeDetectorRef } from "@angular/core";
import {
  Router,
  Event as RouterEvent,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from "@angular/router";
import { MessageboxService } from "../../shared/messagebox/messagebox.service";
import { CoreService } from "../../core/shared/core.service";
import { DLService } from "../../shared/dl.service";
import { NavigationService } from "../../shared/navigation-service";
import { AppInitializationService } from "@app/security/utilities/app-initialization.service";
import { SecurityService } from "@app/security/shared/security.service";
import { AuthService } from "@app/security/utilities/auth.service";
import { DarkModeService } from "../../shared/services/dark-mode.service";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "@app/shared/services/LanguageService/LanguageService";
import { SecurityBLService } from "@app/security/shared/security.bl.service";
import { Language } from "@app/shared/shared-enums";
import { PrimeNGConfig } from "primeng/api";
import moment from "moment";
type AccountingMenuSection = {
  key: string;
  displayName: string;
  segments: string[];
};

type AccountingMenuGroup = {
  key: string;
  displayName: string;
  displaySeq: number;
  segments: string[];
  sections?: AccountingMenuSection[];
};

const ACCOUNTING_NAV_SHELL_SEGMENTS = new Set([
  "organization-setup",
  "chart-of-accounts",
  "cost-centers",
  "system-config",
  "types-classifications",
  "fixed-assets-setup",
  "fixed-assets-management",
]);

const ACCOUNTING_FIXED_ASSETS_SHELL_SEGMENTS = new Set([
  "fixed-assets-setup",
  "fixed-assets-management",
]);

const ACCOUNTING_TAB_CHILD_SEGMENTS = new Set([
  "entities",
  "branches",
  "branch-types",
  "opening-balances",
  "cost-center-types",
  "document-types",
  "tax-codes",
  "subledger-definitions",
  "dimension-types",
  "account-categories",
  "account-sub-types",
  "reason-codes",
  "event-types",
  "cash-bank-accounts",
  "payment-methods",
  "discount-types",
  "bank-movement-types",
  "vendor-types",
  "vendor-groups",
  "legal-entity-types",
  "employee-groups",
  "asset-groups",
  "depreciation-rules",
  "item-categories",
  "item-subcategories",
  "item-master",
  "monthly-depreciation",
  "asset-acquisition",
  "asset-disposal",
]);

const ACCOUNTING_HIDDEN_NAV_SEGMENTS = new Set([
  "posting-rules",
  "posting-center",
  "posting-exceptions",
  "batch-posting",
  "unposted-journals",
  "vendor-payments",
  "payment-scheduling",
  "reports/account-balances-summary",
  "reports/cc-pivot",
  "reports/cash-flow",
  "monthly-depreciation",
  "asset-acquisition",
  "asset-activation",
  "asset-disposal",
  // Cost centers live as a tab inside «الأبعاد التحليلية ومراكز التكلفة»; the old route only redirects there.
  "cost-centers",
  // Hidden for now: the Financial Movements Center takes its place in the menu.
  "mapping",
]);

const ACCOUNTING_ROUTE_DISPLAY_NAMES: Record<string, string> = {
  "organization-setup": "Legal Settings",
  "entities": "Legal Entity",
  "branches": "Branches",
  "branch-types": "Branch Types",
  "fiscal-periods": "Fiscal Periods",
  "opening-balances": "Opening Balances",
  "chart-of-accounts": "Chart of Accounts",
  "cost-centers": "Cost Centers",
  "cost-center-types": "Cost Center Types",
  "dimensions": "Dimensions & Cost Centers",
  "mapping": "Rules & Mapping",
  "system-config": "System Config",
  "document-types": "Document Types",
  "tax-codes": "Tax Codes",
  "subledger-definitions": "Subledger Definitions",
  "account-categories": "Account Classification",
  "dimension-types": "Dimension Types",
  "reason-codes": "Reason Codes",
  "event-types": "Event Types",
  "account-sub-types": "Account Sub Types",
  "item-categories": "Item Categories",
  "item-subcategories": "Item Subcategories",
  "item-master": "Item Master",
  "chart-of-accounts-import": "Chart of Accounts Import",
  "types-classifications": "Types & Classifications",
  "cash-bank-accounts": "Cash/Bank Accounts",
  "payment-methods": "Payment Methods",
  "discount-types": "Discount Types",
  "bank-movement-types": "Bank Movement Types",
  "vendor-types": "Vendor Types",
  "vendor-groups": "Vendor Groups",
  "legal-entity-types": "Juridical Entity Types",
  "journal-entries": "Journal Entries",
  "receipt-vouchers": "Receipt Vouchers",
  "payment-vouchers": "Payment Vouchers",
  "gl-inquiry": "GL Inquiry",
  "posting-workshop": "Posting Center",
  "vendors": "Vendors",
  "vendor-invoices": "Vendor Invoices",
  "payroll-postings": "Payroll Postings",
  "ap-reports": "AP Reports",
  "reports/ap-aging": "AP Aging",
  "reports/ar-aging": "AR Aging",
  "reports/audit-trail": "Audit Trail",
  "reports/trial-balance": "Trial Balance",
  "reports/financial-statement-mapping": "Financial Statement Mapping",
  "reports/account-statement": "Account Statement",
  "reports/income-statement": "Income Statement",
  "reports/balance-sheet": "Balance Sheet",
  "fixed-assets-setup": "Asset Definitions",
  "fixed-assets-management": "Asset Management",
  "asset-register": "Asset Register",
  "asset-groups": "Asset Groups",
  "depreciation-rules": "Depreciation Rules",
  "monthly-depreciation": "Monthly Depreciation",
  "asset-acquisition": "Asset Acquisition",
  "asset-disposal": "Asset Disposal",
  "period-close": "Period Close",
  "year-end-close": "Year End Close",
};

const ACCOUNTING_ROUTE_DISPLAY_SEQ: Record<string, number> = {
  "organization-setup": 10,
  "fiscal-periods": 20,
  "chart-of-accounts": 30,
  "cost-centers": 50,
  "system-config": 60,
  "types-classifications": 70,
  "vendors": 110,
  "chart-of-accounts-import": 120,
  "item-categories": 130,
  "item-subcategories": 140,
  "item-master": 150,
  "fixed-assets-setup": 80,
  "asset-groups": 81,
  "depreciation-rules": 82,
  "asset-register": 91,
  "asset-acquisition": 93,
  "monthly-depreciation": 94,
  "asset-disposal": 95,
  "fixed-assets-management": 90,
  "ap-reports": 260,
  "payroll-postings": 270,
  "reports/trial-balance": 300,
  "gl-inquiry": 310,
  "reports/account-statement": 320,
  "reports/financial-statement-mapping": 330,
  "reports/income-statement": 340,
  "reports/balance-sheet": 350,
  "reports/ap-aging": 380,
  "reports/ar-aging": 390,
  "reports/audit-trail": 400,
  "period-close": 500,
  "year-end-close": 510,
};

const ACCOUNTING_GROUP_TRANSLATION_KEYS: Record<string, string> = {
  settings: "nav.accountingGroups.settings",
  daily: "nav.accountingGroups.daily",
  postingRulesAndMapping: "nav.accountingGroups.postingRulesAndMapping",
  fixedAssets: "nav.accountingGroups.fixedAssets",
  reports: "nav.accountingGroups.reports",
  accountingClosure: "nav.accountingGroups.periodEnd",
};

const ACCOUNTING_SECTION_TRANSLATION_KEYS: Record<string, string> = {
  organizationSettings: "nav.accountingSections.organizationSettings",
  financialStructure: "nav.accountingSections.financialStructure",
  systemDefinitions: "nav.accountingSections.systemDefinitions",
  vendors: "nav.accountingSections.vendors",
  generalLedger: "nav.accountingSections.generalLedger",
  payablesAndPayroll: "nav.accountingSections.payablesAndPayroll",
  rulesAndMappings: "nav.accountingSections.rulesAndMappings",
  postingCenter: "nav.accountingSections.postingCenter",
  fixedAssetSetup: "nav.accountingSections.fixedAssetSetup",
  fixedAssetManagement: "nav.accountingSections.fixedAssetManagement",
  ledgerReports: "nav.accountingSections.ledgerReports",
  agingReports: "nav.accountingSections.agingReports",
  financialStatements: "nav.accountingSections.financialStatements",
  auditReports: "nav.accountingSections.auditReports",
};

const ACCOUNTING_ROUTE_TRANSLATION_KEYS: Record<string, string> = {
  "organization-setup": "nav.accountingRoutes.organizationSetup",
  "entities": "nav.accountingRoutes.entities",
  "branches": "nav.accountingRoutes.branches",
  "branch-types": "nav.accountingRoutes.branchTypes",
  "fiscal-periods": "nav.accountingRoutes.fiscalPeriods",
  "opening-balances": "nav.accountingRoutes.openingBalances",
  "chart-of-accounts": "nav.accountingRoutes.chartOfAccounts",
  "cost-centers": "nav.accountingRoutes.costCenters",
  "cost-center-types": "nav.accountingRoutes.costCenterTypes",
  "dimensions": "nav.accountingRoutes.dimensions",
  "mapping": "nav.accountingRoutes.mapping",
  "movements": "nav.accountingRoutes.movementsHub",
  "system-config": "nav.accountingRoutes.systemConfig",
  "document-types": "nav.accountingRoutes.documentTypes",
  "tax-codes": "nav.accountingRoutes.taxCodes",
  "subledger-definitions": "nav.accountingRoutes.subledgerDefinitions",
  "account-categories": "nav.accountingRoutes.accountCategories",
  "dimension-types": "nav.accountingRoutes.dimensionTypes",
  "reason-codes": "nav.accountingRoutes.reasonCodes",
  "event-types": "nav.accountingRoutes.eventTypes",
  "account-sub-types": "nav.accountingRoutes.accountSubTypes",
  "item-categories": "nav.accountingRoutes.itemCategories",
  "item-subcategories": "nav.accountingRoutes.itemSubCategories",
  "item-master": "nav.accountingRoutes.itemMaster",
  "types-classifications": "nav.accountingRoutes.typesClassifications",
  "cash-bank-accounts": "nav.accountingRoutes.cashBankAccounts",
  "payment-methods": "nav.accountingRoutes.paymentMethods",
  "discount-types": "nav.accountingRoutes.discountTypes",
  "bank-movement-types": "nav.accountingRoutes.bankMovementTypes",
  "vendor-types": "nav.accountingRoutes.vendorTypes",
  "vendor-groups": "nav.accountingRoutes.vendorGroups",
  "legal-entity-types": "nav.accountingRoutes.legalEntityTypes",
  "employee-groups": "nav.accountingRoutes.employeeGroups",
  "journal-entries": "nav.accountingRoutes.journalEntries",
  "receipt-vouchers": "nav.accountingRoutes.receiptVouchers",
  "payment-vouchers": "nav.accountingRoutes.paymentVouchers",
  "gl-inquiry": "nav.accountingRoutes.glInquiry",
  "posting-workshop": "nav.accountingRoutes.postingWorkshop",
  "vendors": "nav.accountingRoutes.vendors",
  "vendor-invoices": "nav.accountingRoutes.vendorInvoices",
  "payroll-postings": "nav.accountingRoutes.payrollPostings",
  "ap-reports": "nav.accountingRoutes.apReports",
  "reports/ap-aging": "nav.accountingRoutes.apAging",
  "reports/ar-aging": "nav.accountingRoutes.arAging",
  "reports/audit-trail": "nav.accountingRoutes.auditTrail",
  "reports/customer-balances": "nav.accountingRoutes.customerBalances",
  "reports/customer-statement": "nav.accountingRoutes.customerStatement",
  "reports/vendor-balances": "nav.accountingRoutes.vendorBalances",
  "reports/trial-balance": "nav.accountingRoutes.trialBalance",
  "reports/financial-statement-mapping": "nav.accountingRoutes.financialStatementMapping",
  "reports/account-statement": "nav.accountingRoutes.accountStatement",
  "reports/income-statement": "nav.accountingRoutes.incomeStatement",
  "reports/balance-sheet": "nav.accountingRoutes.balanceSheet",
  "fixed-assets-setup": "nav.accountingRoutes.fixedAssetsSetup",
  "fixed-assets-management": "nav.accountingRoutes.fixedAssetsManagement",
  "asset-register": "nav.accountingRoutes.assetRegister",
  "asset-groups": "nav.accountingRoutes.assetGroups",
  "depreciation-rules": "nav.accountingRoutes.depreciationRules",
  "monthly-depreciation": "nav.accountingRoutes.monthlyDepreciation",
  "asset-acquisition": "nav.accountingRoutes.assetAcquisition",
  "asset-disposal": "nav.accountingRoutes.assetDisposal",
  "period-close": "nav.accountingRoutes.periodClose",
  "year-end-close": "nav.accountingRoutes.yearEndClose",
};

const SHELL_ROUTE_TRANSLATION_KEYS: Record<string, string> = {
  accounting: "nav.routes.accounting",
  appointment: "nav.routes.appointment",
  appointmentbookinglist: "nav.routes.appointmentBookingList",
  apt01: "nav.routes.appointmentRegister",
  appointmentsvisitqueue: "nav.routes.appointmentsVisitQueue",
  cssd: "nav.routes.cssd",
  adt: "nav.routes.adt",
  adtmain: "nav.routes.adt",
  admissiondischargetransfer: "nav.routes.adt",
  admissiondischargeandtransfer: "nav.routes.adt",
  admissiondischargeamptransfer: "nav.routes.adt",
  admissiondischargeotransfer: "nav.routes.adt",
  admissiondischarge: "nav.routes.adt",
  admissiontransfer: "nav.routes.adt",
  addresults: "nav.routes.addResults",
  assetmaintenance: "nav.routes.assetMaintenance",
  assetmanagement: "nav.routes.assetManagement",
  assetreports: "nav.routes.reports",
  birthlist: "nav.routes.birthList",
  billing: "nav.routes.billing",
  billingreport: "nav.routes.billingReport",
  billingreports: "nav.routes.billingReport",
  bookappointment: "nav.routes.bookAppointment",
  bslip: "nav.routes.bSlip",
  bslips: "nav.routes.bSlip",
  payslip: "nav.routes.payslip",
  payslips: "nav.routes.payslip",
  cancelbills: "nav.routes.cancelBills",
  category: "nav.routes.category",
  categories: "nav.routes.category",
  changedoctor: "nav.routes.changeDoctor",
  claims: "nav.routes.claims",
  claimsmanagement: "nav.routes.claimsManagement",
  clinical: "nav.routes.clinical",
  collectsample: "nav.routes.collectSample",
  corecfgparameters: "nav.routes.coreCfgParameters",
  codereceipt: "nav.routes.goodsReceipt",
  codesreceipt: "nav.routes.goodsReceipt",
  creditcompanies: "nav.routes.creditCompanies",
  dashboard: "nav.routes.dashboard",
  deathlist: "nav.routes.deathList",
  departments: "nav.routes.departments",
  dispensary: "nav.routes.dispensary",
  depreciationdiscarding: "nav.routes.depreciationDiscarding",
  depreciationanddiscarding: "nav.routes.depreciationDiscarding",
  dischargelist: "nav.routes.dischargeList",
  dischargesummary: "nav.routes.dischargeSummary",
  disbursedreceipts: "nav.routes.disbursedReceipts",
  dispenserequest: "nav.routes.dispenseRequest",
  dispenserequests: "nav.routes.dispenseRequest",
  dispensaryrequests: "nav.routes.dispensaryRequests",
  documentmanagement: "nav.routes.documentManagement",
  doctor: "nav.routes.doctor",
  doctors: "nav.routes.doctor",
  drugrequest: "nav.routes.drugRequisition",
  drugrequests: "nav.routes.drugRequisition",
  drugrequisition: "nav.routes.drugRequisition",
  drugrequisitions: "nav.routes.drugRequisition",
  emergency: "nav.routes.emergency",
  eligibility: "nav.routes.eligibility",
  eligibilityhistory: "nav.routes.eligibilityHistory",
  eligiblehistory: "nav.routes.eligibilityHistory",
  employeesalary: "nav.routes.employeeSalary",
  attendance: "HR.Navigation.Attendance",
  manageattendance: "HR.Navigation.ManageAttendance",
  adminattendance: "HR.Navigation.AdminAttendance",
  employeeattendance: "HR.Navigation.EmployeeAttendance",
  employeemanage: "HR.Navigation.EmployeeManagement",
  employeemanagement: "HR.Navigation.EmployeeManagement",
  manageemployee: "HR.Navigation.Employees",
  manageemployeerole: "HR.Navigation.EmployeeRoles",
  manageemployeetype: "HR.Navigation.EmployeeTypes",
  holiday: "HR.Navigation.Holiday",
  officialholiday: "HR.Navigation.OfficialHoliday",
  weekendholiday: "HR.Navigation.WeekendHoliday",
  weekendholidays: "HR.Navigation.WeekendHoliday",
  weekend: "HR.Navigation.WeekendHoliday",
  leaves: "HR.Navigation.Leaves",
  leaveconfiguration: "HR.Navigation.LeaveConfiguration",
  adminleave: "HR.Navigation.AdminLeave",
  employeeleave: "HR.Navigation.EmployeeLeave",
  externalreferrals: "nav.routes.externalReferrals",
  externallabs: "nav.routes.externalLabs",
  externaltests: "nav.routes.externalTests",
  fixedassetmovement: "nav.routes.fixedAssetMovement",
  fraction: "nav.routes.fraction",
  fixedassets: "nav.routes.fixedAssets",
  fixedasset: "nav.routes.fixedAssets",
  fixedassetreports: "nav.routes.fixedAssetReports",
  finalreports: "nav.routes.finalReports",
  geographiclocation: "nav.routes.geographicLocation",
  geolocation: "nav.routes.geographicLocation",
  goodsarrival: "nav.routes.goodsArrival",
  goodsarrivalnotification: "nav.routes.goodsArrival",
  goodsarrivalnotifications: "nav.routes.goodsArrival",
  goodarrivalnotification: "nav.routes.goodsArrival",
  goodsarrivalnote: "nav.routes.goodsArrival",
  goodsreceipt: "nav.routes.goodsReceipt",
  goodsreceipts: "nav.routes.goodsReceipt",
  govinsurance: "nav.routes.govInsurance",
  governmentinsurance: "nav.routes.govInsurance",
  govinsurancevisitlist: "nav.routes.visitList",
  handover: "nav.routes.handOver",
  handovers: "nav.routes.handOver",
  helpdesk: "nav.routes.helpdesk",
  hrmanagement: "nav.routes.hrManagement",
  hrmanagment: "nav.routes.hrManagement",
  humanresource: "nav.routes.hrManagement",
  humanresourcemanagement: "nav.routes.hrManagement",
  humanresources: "nav.routes.hrManagement",
  shifitsandschedule: "HR.Navigation.ShiftsAndSchedule",
  shiftsandschedule: "HR.Navigation.ShiftsAndSchedule",
  shiftandschedule: "HR.Navigation.ShiftsAndSchedule",
  shiftmanagment: "HR.Navigation.ShiftManagement",
  shiftmanagement: "HR.Navigation.ShiftManagement",
  scheduletiming: "HR.Navigation.ScheduleTiming",
  incentive: "nav.routes.incentive",
  incentivetransaction: "nav.routes.incentiveTransaction",
  incentivetransactions: "nav.routes.incentiveTransaction",
  inpatientdepartment: "nav.routes.inpatientDepartment",
  inpatient: "nav.routes.inpatient",
  inpatientlist: "MedicalRecords.Navigation.InpatientList",
  insurances: "nav.routes.insurances",
  insuranceadmin: "nav.routes.insuranceAdmin",
  insuranceadministration: "nav.routes.insuranceAdmin",
  internal: "nav.routes.internal",
  inventory: "nav.routes.inventory",
  inventoryreports: "nav.routes.inventoryReports",
  inventoryverification: "nav.routes.inventoryVerification",
  ipdbilling: "nav.routes.ipdBilling",
  itemcategory: "nav.routes.category",
  itemcategories: "nav.routes.category",
  itemsubcategory: "nav.routes.subCategory",
  itemsubcategories: "nav.routes.subCategory",
  itemtype: "nav.routes.itemType",
  itemtypes: "nav.routes.itemType",
  laboratory: "nav.routes.laboratory",
  lis: "nav.routes.lis",
  maternity: "nav.routes.maternity",
  maternitylist: "nav.routes.maternityList",
  managevisits: "nav.routes.manageVisits",
  meteorizedsalesview: "nav.routes.meteorizedSalesView",
  mr: "nav.routes.medicalRecords",
  mrinpatientlist: "MedicalRecords.Navigation.InpatientList",
  mroutpatientlist: "MedicalRecords.Navigation.OutpatientList",
  mrpatient: "nav.routes.mrPatient",
  mrreports: "nav.routes.mrReports",
  medicalrecords: "nav.routes.medicalRecords",
  notification: "nav.routes.notification",
  nursing: "nav.routes.nursing",
  newvisit: "nav.routes.newVisit",
  operationtheatre: "nav.routes.operationTheatre",
  operationtheater: "nav.routes.operationTheatre",
  operationroom: "nav.routes.operationTheatre",
  order: "nav.routes.order",
  obd: "nav.routes.obd",
  outpatient: "nav.routes.outpatient",
  outpatientlist: "MedicalRecords.Navigation.OutpatientList",
  patientlist: "nav.routes.patientList",
  patient: "nav.routes.patient",
  searchpatient: "nav.routes.patientFinancialCenter",
  patientslist: "nav.routes.patientsList",
  pendingreports: "nav.routes.pendingReports",
  payroll: "nav.routes.payroll",
  payrolladdition: "nav.routes.payrollAddition",
  payrolladditions: "nav.routes.payrollAddition",
  payrollitemsaddition: "nav.routes.payrollAddition",
  payrollitemsadditions: "nav.routes.payrollAddition",
  payrolldeduction: "nav.routes.payrollDeduction",
  payrolldeductions: "nav.routes.payrollDeduction",
  payrollitemsdeduction: "nav.routes.payrollDeduction",
  payrollitemsdeductions: "nav.routes.payrollDeduction",
  payrollitemaddition: "nav.routes.payrollAddition",
  payrollitemadditions: "nav.routes.payrollAddition",
  payrollitemdeduction: "nav.routes.payrollDeduction",
  payrollitemdeductions: "nav.routes.payrollDeduction",
  payrollitems: "nav.routes.payrollItems",
  payrollsetting: "nav.routes.payrollSettings",
  payrollsettings: "nav.routes.payrollSettings",
  payment: "nav.routes.payment",
  pharmacy: "nav.routes.pharmacy",
  pharmacyreport: "nav.routes.pharmacyReports",
  pharmacyreports: "nav.routes.pharmacyReports",
  pharmacysetting: "nav.routes.pharmacySettings",
  pharmacysettings: "nav.routes.pharmacySettings",
  pharmacysupplier: "nav.routes.supplier",
  preauth: "nav.routes.preauth",
  preauthorization: "nav.routes.preauth",
  preauthhistory: "nav.routes.preauthHistory",
  preauthenticationhistory: "nav.routes.preauthHistory",
  pricelistmanagement: "nav.routes.priceListManagement",
  pricelistupload: "nav.routes.priceListUpload",
  pricelist: "nav.routes.priceListManagement",
  prescription: "nav.routes.prescription",
  professionalreceipts: "nav.routes.professionalReceipts",
  provisionalitems: "nav.routes.provisionalItems",
  purchaserequest: "nav.routes.purchaseRequest",
  purchaserequests: "nav.routes.purchaseRequest",
  purchaseorder: "nav.routes.purchaseOrder",
  purchaseorders: "nav.routes.purchaseOrder",
  procurement: "nav.routes.procurement",
  procurementreports: "nav.routes.procurementReports",
  qinformation: "nav.routes.queueInformation",
  queueinformation: "nav.routes.queueInformation",
  quotation: "nav.routes.quotation",
  requestforquotation: "nav.routes.quotation",
  radiology: "nav.routes.radiology",
  registerpatient: "nav.routes.registerPatient",
  report: "nav.routes.reports",
  reports: "nav.routes.reports",
  queuemngmt: "nav.routes.queueManagement",
  radiologysettings: "nav.routes.radiologySettings",
  reportdispatch: "nav.routes.reportDispatch",
  requisitionlist: "nav.routes.requisitionList",
  receptionoperations: "nav.routes.appointmentsVisitQueue",
  requests: "nav.routes.requests",
  returntovendor: "nav.routes.returnToVendor",
  rx: "nav.routes.rx",
  sale: "nav.routes.sale",
  samplecollection: "nav.routes.collectSample",
  settlementreceipts: "nav.routes.settlementReceipts",
  settlements: "nav.routes.settlements",
  setting: "nav.routes.setting",
  settings: "nav.routes.setting",
  security: "nav.routes.security",
  socialservice: "nav.routes.socialService",
  sterilization: "nav.routes.sterilization",
  substore: "nav.routes.subStore",
  substorepharmacy: "nav.routes.subStorePharmacy",
  substorerequest: "nav.routes.substoreRequest",
  substorerequests: "nav.routes.substoreRequest",
  submitpreauthorization: "nav.routes.submitPreAuthorization",
  submitpreauthentication: "nav.routes.submitPreAuthorization",
  supplier: "nav.routes.supplier",
  store: "nav.routes.store",
  stock: "nav.routes.stock",
  stockreports: "nav.routes.inventoryReports",
  systemadmin: "nav.routes.systemAdmin",
  turnedinvoice: "nav.routes.turnedInvoice",
  transaction: "nav.routes.transaction",
  vaccination: "nav.routes.vaccination",
  visitlist: "nav.routes.visitList",
  wardbilling: "nav.routes.wardBilling",
  wardsupply: "nav.routes.wardSupply",
};

const ACCOUNTING_MENU_GROUPS: AccountingMenuGroup[] = [
  {
    key: "settings",
    displayName: "Settings / Master Data",
    displaySeq: 1,
    segments: [
      "organization-setup", "fiscal-periods", "chart-of-accounts", "cost-centers",
      "account-categories", "account-sub-types",
      "system-config", "types-classifications",
      "entities", "branches", "branch-types", "opening-balances", "cost-center-types",
      "dimensions", "tax-codes", "cash-bank-accounts",
      "document-types", "subledger-definitions",
      "dimension-types", "reason-codes", "event-types",
      "payment-methods", "discount-types",
      "bank-movement-types", "vendors", "vendor-types", "vendor-groups",
      "legal-entity-types", "employee-groups",
    ],
    sections: [
      {
        key: "organizationSettings",
        displayName: "Organization Settings",
        segments: ["organization-setup", "fiscal-periods"],
      },
      {
        key: "financialStructure",
        displayName: "Financial Structure",
        segments: ["chart-of-accounts", "cost-centers", "dimensions"],
      },
      {
        key: "systemDefinitions",
        displayName: "System Definitions",
        segments: ["system-config", "types-classifications"],
      },
      {
        key: "vendors",
        displayName: "Vendors",
        segments: ["vendors"],
      },
    ],
  },
  {
    key: "daily",
    displayName: "Daily Operations",
    displaySeq: 3,
    segments: [
      "journal-entries", "receipt-vouchers", "payment-vouchers",
      "vendor-invoices", "payroll-postings",
    ],
    sections: [
      {
        key: "generalLedger",
        displayName: "General Ledger",
        segments: ["journal-entries", "receipt-vouchers", "payment-vouchers"],
      },
      {
        key: "payablesAndPayroll",
        displayName: "Payables And Payroll",
        segments: ["vendor-invoices", "payroll-postings"],
      },
    ],
  },
  {
    key: "postingRulesAndMapping",
    displayName: "Posting Rules And Mapping Center",
    displaySeq: 4,
    segments: ["movements", "mapping", "posting-rules", "posting-workshop", "posting-center", "reports/financial-statement-mapping"],
    sections: [
      {
        key: "rulesAndMappings",
        displayName: "Rules And Mappings",
        segments: ["movements", "mapping", "posting-rules", "reports/financial-statement-mapping"],
      },
      {
        key: "postingCenter",
        displayName: "Posting Center",
        segments: ["posting-workshop", "posting-center"],
      },
    ],
  },
  {
    key: "fixedAssets",
    displayName: "Fixed Assets",
    displaySeq: 5,
    segments: [
      "fixed-assets-setup", "asset-groups", "depreciation-rules", "item-categories", "item-subcategories",
      "fixed-assets-management", "asset-register", "item-master", "asset-acquisition", "monthly-depreciation", "asset-disposal",
    ],
  },
  {
    key: "reports",
    displayName: "Reports",
    displaySeq: 6,
    segments: [
      "gl-inquiry", "ap-reports",
      "reports", "reports/ap-aging", "reports/ar-aging",
      "reports/audit-trail", "reports/customer-balances", "reports/customer-statement", "reports/vendor-balances", "reports/vendor-statement", "reports/trial-balance",
      "reports/account-statement", "reports/income-statement", "reports/balance-sheet",
    ],
    sections: [
      {
        key: "ledgerReports",
        displayName: "Ledger Reports",
        segments: ["gl-inquiry", "reports/trial-balance", "reports/account-statement"],
      },
      {
        key: "agingReports",
        displayName: "Aging And Balances",
        segments: ["ap-reports", "reports/ap-aging", "reports/ar-aging", "reports/customer-balances", "reports/customer-statement", "reports/vendor-balances", "reports/vendor-statement"],
      },
      {
        key: "financialStatements",
        displayName: "Financial Statements",
        segments: ["reports/income-statement", "reports/balance-sheet"],
      },
      {
        key: "auditReports",
        displayName: "Audit",
        segments: ["reports/audit-trail"],
      },
    ],
  },
  {
    key: "accountingClosure",
    displayName: "Accounting Closure",
    displaySeq: 7,
    segments: [
      "period-close", "year-end-close",
    ],
  },
];

@Component({
  selector: "app-layout",
  standalone: false,
  templateUrl: "./app-layout.component.html",
  styles: [`
    .sidebar-menu .accounting-menu-section-label {
      color: #9aa0a6;
      display: block;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: .7px;
      padding: 8px 20px 4px 72px;
      text-transform: uppercase;
    }

    /* PLAN: FE-PLAN-2026-08-01-systemconfiguration | REQ: REQ-SYSTEMCONFIGURATION-017 */
    .sidebar-menu .settings-nested-menu-toggle {
      padding-inline-end: calc(var(--sp-20) + var(--sp-20));
    }

    .sidebar-menu .settings-nested-menu-label {
      min-inline-size: 0;
      overflow-wrap: anywhere;
      white-space: normal;
    }
  `],
})
export class AppLayoutComponent {

  public openMenus: { [key: string]: boolean } = {};
  public showDatePopup = false;
  public empPre = { np: false, en: false };
  private selectedDatePref = "";

  loading = true;
  loadingScreen = false;

  searchText: string = "";
  allRoutes: any[] = [];
  filteredRoutes: any[] = [];

  languages = [
    {
      code: Language.ar,
      label: "العربية",
      icon: "./public/public/img/dhs/Ar.svg",
    },
    {
      code: Language.en,
      label: "English",
      icon: "./public/public/img/dhs/En.svg",
    },
  ];

  currentLang: string = "en";

  constructor(
    public router: Router,
    private dlService: DLService,
    private messageBoxService: MessageboxService,
    private changeDetector: ChangeDetectorRef,
    public coreService: CoreService,
    public navService: NavigationService,
    private appInitializationService: AppInitializationService,
    public securityService: SecurityService,
    private authService: AuthService,
    public darkMode: DarkModeService,
    public translate: TranslateService,
    private languageService: LanguageService,
    private readonly securityBlService: SecurityBLService,
    private primengConfig: PrimeNGConfig,
  ) {
    translate.setDefaultLang(Language.en);

    this.router.events.subscribe((event: RouterEvent) =>
      this.navigationInterceptor(event),
    );

    this.appInitializationService.initialize();
  }


  ngOnInit() {

    this.allRoutes = this.withCommissionMenuGroups(this.withAccountingMenuGroups(this.securityService.GetAllValidRoutes()));
    this.filteredRoutes = this.allRoutes;

    const savedLang = this.languageService.getLanguage();

    this.currentLang = savedLang;

    this.applyLanguage(savedLang);
  }

  // =========================
  // LANGUAGE SYSTEM (CLEAN)
  // =========================

  changeLang(lang: string) {
    this.currentLang = lang;

    this.languageService.setLanguage(lang);

    this.applyLanguage(lang);
  }

  private applyLanguage(lang: string) {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === Language.ar ? "rtl" : "ltr";

    this.loadBootstrap(lang);

    this.setArabicStyle(lang === Language.ar);
    // this.setPrimeNGCalendar(lang);
    // this.setMomentLocale(lang);
  }
  private setPrimeNGCalendar(lang: string) {
    if (lang === Language.ar) {
      this.primengConfig.setTranslation({
        dayNames: [
          "الأحد",
          "الإثنين",
          "الثلاثاء",
          "الأربعاء",
          "الخميس",
          "الجمعة",
          "السبت",
        ],
        dayNamesShort: ["أحد", "إثن", "ثلا", "أرب", "خم", "جم", "سبت"],
        dayNamesMin: ["ح", "ن", "ث", "ر", "خ", "ج", "س"],

        monthNames: [
          "يناير",
          "فبراير",
          "مارس",
          "أبريل",
          "مايو",
          "يونيو",
          "يوليو",
          "أغسطس",
          "سبتمبر",
          "أكتوبر",
          "نوفمبر",
          "ديسمبر",
        ],

        monthNamesShort: [
          "ينا",
          "فبر",
          "مار",
          "أبر",
          "ماي",
          "يون",
          "يول",
          "أغس",
          "سبت",
          "أكت",
          "نوف",
          "ديس",
        ],

        today: "اليوم",
        clear: "مسح",
      });
    } else {
      this.primengConfig.setTranslation({
        dayNames: [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],

        monthNames: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],

        monthNamesShort: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],

        today: "Today",
        clear: "Clear",
      });
    }
  }
  private setMomentLocale(lang: string) {
    if (lang === Language.ar) {
      moment.locale("ar");
    } else {
      moment.locale("en-gb");
    }
  }
  private loadBootstrap(lang: string) {
    const href =
      lang === Language.ar
        ? "public/themes/theme-default/bootstrap.rtl.min.css"
        : "public/themes/theme-default/bootstrap.min.css";

    let existing = document.getElementById("bootstrap-style");

    if (existing) {
      existing.setAttribute("href", href);
    } else {
      let link = document.createElement("link");
      link.id = "bootstrap-style";
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  }

  private setArabicStyle(enable: boolean) {
    const existing = document.getElementById("arabic-style");

    if (enable && !existing) {
      let link = document.createElement("link");
      link.id = "arabic-style";
      link.rel = "stylesheet";
      link.href = "public/themes/theme-default/arabic.css";
      document.head.appendChild(link);
    } else if (!enable && existing) {
      existing.remove();
    }
  }

  // =========================
  // ROUTING + UI
  // =========================

  onSearchChange() {
    const search = this.searchText.toLowerCase().trim();

    if (!search) {
      this.filteredRoutes = this.allRoutes;
      return;
    }

    this.filteredRoutes = this.allRoutes
      .map((route) => this.filterRouteTree(route, search))
      .filter(Boolean);
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  toggleSubMenu(menuName: string, event: Event) {
    event.preventDefault();
    this.openMenus[menuName] = !this.openMenus[menuName];
  }

  isAccountingRoute(route: any): boolean {
    const routePath = `${route?.UrlFullPath ?? route?.RouterLink ?? route?.DisplayName ?? ""}`.toLowerCase();
    return routePath === "accounting" || routePath === "/accounting";
  }

  isCommissionRoute(route: any): boolean {
    return this.normalizedRoutePath(route) === "commission";
  }

  // Present only routes already supplied by RBAC; grouping does not grant access.
  private withCommissionMenuGroups(routes: any[]): any[] {
    const labels: Record<string, { name: string; key: string; order: number }> = {
      "commission/earnings": { name: "Commission Earnings", key: "Earnings", order: 3 },

      "commission/reports": { name: "Reports", key: "Reports", order: 5 },
      "commission/dashboard": { name: "Commission Dashboard", key: "Dashboard", order: 1 },
      "commission/rules": { name: "Commission Rule Management", key: "RuleManagement", order: 1 },
      "commission/assignments": { name: "Doctor Commission Management", key: "Doctors", order: 2 },
    };
    return routes.map(route => {
      if (!this.isCommissionRoute(route)) return route;
      const children = (route.ChildRoutes ?? []).flatMap((child: any) =>
        this.normalizedRoutePath(child) === "commission/configuration" || child.CommissionMenuGroup
          ? child.ChildRoutes ?? [] : [child]);
      const direct: any[] = [];
      const configuration: any[] = [];
      for (const child of children) {
        const path = this.normalizedRoutePath(child);
        if (path === "commission/approvals") continue;

        const label = labels[path];
        const item = label ? { ...child, DisplayName: label.name, DisplaySeq: label.order,
          CommissionNavigationKey: "Commission.Navigation." + label.key } : child;
        if (path === "commission/rules" || path === "commission/assignments") {
          if (child.DefaultShow !== false && child.IsActive !== false) configuration.push(item);
        } else direct.push(item);
      }
      const childRoutes = this.sortMenuRoutes(direct);
      if (configuration.length) childRoutes.push({
        RouteId: "commission-configuration", DisplayName: "Configuration", DisplaySeq: 2,
        UrlFullPath: "", RouterLink: "", DefaultShow: true, CommissionMenuGroup: true,
        CommissionNavigationKey: "Commission.Navigation.Configuration",
        ChildRoutes: this.sortMenuRoutes(configuration),
      });
      return { ...route, DisplayName: "Commission Management",
        CommissionNavigationKey: "Commission.Navigation.Root", ChildRoutes: this.sortMenuRoutes(childRoutes) };
    });
  }

  hasVisibleChildRoutes(route: any): boolean {
    return route?.ChildRoutes?.some((child: any) => child?.DisplayName?.length > 0 && child.DefaultShow !== false) === true;
  }

  isMenuSection(route: any): boolean {
    return route?.IsMenuSection === true;
  }

  childMenuKey(parentRoute: any, childRoute: any): string {
    return `${parentRoute?.DisplayName ?? "menu"}:${childRoute?.DisplayName ?? "submenu"}`;
  }

  routeLink(route: any): string {
    const path = `${route?.UrlFullPath ?? route?.RouterLink ?? ""}`.trim();
    return path.startsWith("/") ? path : `/${path}`;
  }

  navigationLabel(route: any): string {
    const key = this.navigationTranslationKey(route);
    if (!key) {
      return route?.DisplayName ?? "";
    }

    const translated = this.translate.instant(key);
    return translated === key ? (route?.DisplayName ?? "") : translated;
  }

  private filterRouteTree(route: any, search: string): any | null {
    const matchParent = route.DisplayName?.toLowerCase().includes(search);
    const children = route.ChildRoutes
      ?.map((child: any) => this.filterRouteTree(child, search))
      .filter(Boolean);

    if (matchParent) {
      return { ...route, ChildRoutes: route.ChildRoutes };
    }

    if (children?.length > 0) {
      return { ...route, ChildRoutes: children };
    }

    return null;
  }

  private withAccountingMenuGroups(routes: any[]): any[] {
    const hasAccountingRoot = routes.some((route) => this.isAccountingRoute(route));
    const standaloneAccountingRoutes = routes.filter((route) => this.isStandaloneAccountingRoute(route));
    const sourceRoutes = hasAccountingRoot || standaloneAccountingRoutes.length === 0
      ? routes
      : [
        ...routes,
        {
          DisplayName: "Accounting",
          DisplaySeq: 55,
          UrlFullPath: "/Accounting",
          RouterLink: "/Accounting",
          ChildRoutes: standaloneAccountingRoutes,
        },
      ];

    return sourceRoutes.map((route) => {
      if (!this.isAccountingRoute(route)) {
        return route;
      }

      const accountingRoute = this.mergeAccountingChildRoutes(route, standaloneAccountingRoutes);
      if (!accountingRoute.ChildRoutes?.length) {
        return accountingRoute;
      }

      const flatRoutes = this.flattenMenuRoutes(accountingRoute.ChildRoutes);
      const directRoutes: any[] = [];
      const groupedRoutes = ACCOUNTING_MENU_GROUPS.map((group) => this.createAccountingGroupRoute(accountingRoute, group));

      flatRoutes.forEach((childRoute) => {
        const targetGroup = this.accountingGroupForRoute(childRoute);
        if (!targetGroup) {
          directRoutes.push(childRoute);
          return;
        }

        const groupRoute = groupedRoutes.find((candidate) => candidate.MenuGroupKey === targetGroup.key);
        if (!groupRoute) {
          directRoutes.push(childRoute);
          return;
        }

        const targetSection = targetGroup.sections?.find((section) => this.accountingSectionMatchesRoute(section, childRoute));
        if (targetSection) {
          const sectionRoute = groupRoute.ChildRoutes.find((candidate: any) => candidate.MenuSectionKey === targetSection.key);
          sectionRoute?.ChildRoutes.push(childRoute);
        } else {
          groupRoute.ChildRoutes.push(childRoute);
        }
      });

      groupedRoutes.forEach((groupRoute) => {
        groupRoute.ChildRoutes = groupRoute.ChildRoutes
          .map((childRoute: any) => childRoute.IsMenuSection
            ? { ...childRoute, ChildRoutes: this.sortMenuRoutes(childRoute.ChildRoutes) }
            : childRoute)
          .filter((childRoute: any) => !childRoute.IsMenuSection || childRoute.ChildRoutes.length > 0);
      });

      return {
        ...accountingRoute,
        ChildRoutes: [
          ...groupedRoutes.filter((groupRoute) => groupRoute.ChildRoutes.length > 0),
          ...this.sortMenuRoutes(directRoutes),
        ],
      };
    }).filter((route) => !this.isStandaloneAccountingRoute(route));
  }

  private mergeAccountingChildRoutes(route: any, standaloneAccountingRoutes: any[]): any {
    if (!standaloneAccountingRoutes.length) {
      return route;
    }

    const childRoutes = [...(route.ChildRoutes ?? [])];
    standaloneAccountingRoutes.forEach((standaloneRoute) => {
      const standalonePath = this.normalizedRoutePath(standaloneRoute);
      const exists = childRoutes.some((childRoute) => this.normalizedRoutePath(childRoute) === standalonePath);
      if (!exists) {
        childRoutes.push(standaloneRoute);
      }
    });

    return {
      ...route,
      ChildRoutes: childRoutes,
    };
  }

  private isStandaloneAccountingRoute(route: any): boolean {
    return this.normalizedRoutePath(route).startsWith("accounting/");
  }

  private normalizedRoutePath(route: any): string {
    return `${route?.UrlFullPath ?? route?.RouterLink ?? ""}`.toLowerCase().replace(/^\/+/, "").split("?")[0].split("#")[0];
  }

  private isFixedAssetsShellChildRoute(route: any): boolean {
    const normalizedPath = this.normalizedRoutePath(route);
    return normalizedPath.startsWith("accounting/fixed-assets-setup/") ||
      normalizedPath.startsWith("accounting/fixed-assets-management/");
  }

  private navigationTranslationKey(route: any): string | null {
    if (route?.CommissionNavigationKey) return route.CommissionNavigationKey;
    const groupKey = `${route?.MenuGroupKey ?? ""}`;
    if (groupKey && ACCOUNTING_GROUP_TRANSLATION_KEYS[groupKey]) {
      return ACCOUNTING_GROUP_TRANSLATION_KEYS[groupKey];
    }

    const sectionKey = `${route?.MenuSectionKey ?? ""}`;
    if (sectionKey && ACCOUNTING_SECTION_TRANSLATION_KEYS[sectionKey]) {
      return ACCOUNTING_SECTION_TRANSLATION_KEYS[sectionKey];
    }

    const segment = this.accountingRouteSegment(route);
    return ACCOUNTING_ROUTE_TRANSLATION_KEYS[segment] ?? this.shellRouteTranslationKey(route);
  }

  private shellRouteTranslationKey(route: any): string | null {
    const candidates = [
      route?.DisplayName,
      this.lastRoutePathSegment(route?.UrlFullPath),
      this.lastRoutePathSegment(route?.RouterLink),
    ];

    for (const candidate of candidates) {
      const key = this.normalizedShellRouteLabel(candidate);
      if (key && SHELL_ROUTE_TRANSLATION_KEYS[key]) {
        return SHELL_ROUTE_TRANSLATION_KEYS[key];
      }
    }

    return null;
  }

  private lastRoutePathSegment(path: any): string {
    return `${path ?? ""}`.split("?")[0].split("#")[0].split("/").filter(Boolean).pop() ?? "";
  }

  private normalizedShellRouteLabel(label: any): string {
    return `${label ?? ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  private createAccountingGroupRoute(parentRoute: any, group: AccountingMenuGroup): any {
    return {
      ...parentRoute,
      RouteId: `accounting-${group.key}`,
      DisplayName: group.displayName,
      DisplaySeq: group.displaySeq,
      UrlFullPath: "",
      RouterLink: "",
      MenuGroupKey: group.key,
      ChildRoutes: group.sections?.map((section) => ({
        ...parentRoute,
        RouteId: `accounting-${group.key}-${section.key}`,
        DisplayName: section.displayName,
        DisplaySeq: 0,
        UrlFullPath: "",
        RouterLink: "",
        IsMenuSection: true,
        MenuSectionKey: section.key,
        ChildRoutes: [],
      })) ?? [],
    };
  }

  private flattenMenuRoutes(routes: any[]): any[] {
    return routes.flatMap((route) => {
      const normalizedRoute = this.normalizeAccountingMenuRoute(route);
      const segment = this.accountingRouteSegment(normalizedRoute);
      const isFixedAssetsChild = this.isFixedAssetsShellChildRoute(normalizedRoute);
      const visibleChildren = route.ChildRoutes?.filter((child: any) => child?.DefaultShow !== false) ?? [];
      if (ACCOUNTING_FIXED_ASSETS_SHELL_SEGMENTS.has(segment) && visibleChildren.length > 0) {
        return this.flattenMenuRoutes(visibleChildren);
      }

      if (ACCOUNTING_HIDDEN_NAV_SEGMENTS.has(segment) && !isFixedAssetsChild) {
        return [];
      }

      if (ACCOUNTING_NAV_SHELL_SEGMENTS.has(segment)) {
        return [normalizedRoute];
      }

      if (ACCOUNTING_TAB_CHILD_SEGMENTS.has(segment) && !isFixedAssetsChild) {
        return [];
      }

      if (visibleChildren.length === 0) {
        return [normalizedRoute];
      }

      return this.flattenMenuRoutes(visibleChildren);
    });
  }

  private normalizeAccountingMenuRoute(route: any): any {
    const segment = this.accountingRouteSegment(route);
    const displayName = ACCOUNTING_ROUTE_DISPLAY_NAMES[segment];
    if (!displayName) {
      return route;
    }

    return {
      ...route,
      DisplayName: displayName,
      DisplaySeq: this.isFixedAssetsShellChildRoute(route)
        ? route.DisplaySeq
        : ACCOUNTING_ROUTE_DISPLAY_SEQ[segment] ?? route.DisplaySeq,
    };
  }

  private accountingGroupForRoute(route: any): AccountingMenuGroup | undefined {
    const displayName = `${route?.DisplayName ?? ""}`.toLowerCase().replace(/\s+/g, "");
    const legacyGroupAliases: Record<string, string> = {
      settings: "settings",
      systemsettings: "settings",
      transaction: "daily",
      dailyoperations: "daily",
      accountspayable: "daily",
      postingrulesandmapping: "postingRulesAndMapping",
      postingrulesandmappingcenter: "postingRulesAndMapping",
      fixedassets: "fixedAssets",
      accountingclosures: "accountingClosure",
      periodend: "accountingClosure",
      reports: "reports",
    };
    const alias = legacyGroupAliases[displayName];
    if (alias) {
      return ACCOUNTING_MENU_GROUPS.find((group) => group.key === alias);
    }

    const segment = this.accountingRouteSegment(route);
    return ACCOUNTING_MENU_GROUPS.find((group) => group.key.toLowerCase() === segment || group.segments.includes(segment));
  }

  private accountingSectionMatchesRoute(section: AccountingMenuSection, route: any): boolean {
    return section.segments.includes(this.accountingRouteSegment(route));
  }

  private accountingRouteSegment(route: any): string {
    const path = `${route?.UrlFullPath ?? route?.RouterLink ?? ""}`.toLowerCase();
    const normalizedPath = path.replace(/^\/+/, "").split("?")[0].split("#")[0];
    const segments = normalizedPath.split("/");
    if (segments[1] === "reports" && segments[2]) {
      return `reports/${segments[2]}`;
    }

    if (segments[1] === "chart-of-accounts" && segments[2] === "import") {
      return "chart-of-accounts-import";
    }

    if (normalizedPath.endsWith("/legal-entity-types")) {
      return "legal-entity-types";
    }

    if (
      (segments[1] === "fixed-assets-setup" || segments[1] === "fixed-assets-management") &&
      segments[2]
    ) {
      return segments[2];
    }

    return segments[1] ?? "";
  }

  private sortMenuRoutes(routes: any[]): any[] {
    return [...routes].sort((a, b) => (a.DisplaySeq ?? 0) - (b.DisplaySeq ?? 0));
  }

  navigationInterceptor(event: RouterEvent) {

    if (event instanceof NavigationStart) {
      this.loading = true;
    }

    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      this.loading = false;
    }
  }

  onActivate() {
    window.scroll(0, 0);
  }

  // =========================
  // DATE PREF + OTHER LOGIC
  // =========================

  openShowDatePreference() {
    this.showDatePopup = true;
  }

  Close() {
    this.showDatePopup = false;
  }

  ChangeDatePreference(e: any) {
    const type = e.target.name === "AD" ? "en" : "np";

    this.DatePreferenceData(type);

    const calendar = this.translate.instant(
      type === "en"
        ? "shell.datePreference.englishAd"
        : "shell.datePreference.nepaliBs",
    );

    this.messageBoxService.showMessage("notice", [
      this.translate.instant("shell.datePreference.savedLocally", { calendar }),
    ]);
  }

  DatePreferenceData(type: string) {
    this.empPre = {
      np: type === "np",
      en: type === "en",
    };

    this.selectedDatePref = type;
    this.coreService.DatePreference = type;
  }

  SaveEmpPref() {
    this.dlService
      .Add(this.selectedDatePref, "Core?reqType=post-emp-datepreference")
      .subscribe((res) => {
        if (res.Status === "OK") {
          this.coreService.DatePreference =
            res.Results?.PreferenceValue ?? null;

          this.DatePreferenceData(this.coreService.DatePreference);

          this.messageBoxService.showMessage("success", [
            this.translate.instant("shell.datePreference.saved"),
          ]);

          this.Close();
        }
      });
  }

  DownloadUserManual() {
    this.dlService.ReadExcel("/Home/GetUserManual").subscribe(
      (data) => {
        const blob: any = data;
        const a = document.createElement("a");

        a.href = URL.createObjectURL(blob);
        a.download = "HIS-UserManual.pdf";

        document.body.appendChild(a);
        a.click();
      },
      (err) => alert(err),
    );
  }

  LogoutFromAplication() {
    sessionStorage.removeItem("isLandingVisited");
    localStorage.removeItem("isLandingVisitedNewTab");
    localStorage.removeItem("selectedLabCategory");

    localStorage.setItem("logout-event", "logout" + Math.random());

    this.authService.logout();
    this.appInitializationService.reset();
  }

  logError(err: any) {
    console.error(err);
  }
}

